import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { CopyButton } from "@/components/admin/CopyButton";
import { minisiteInstructions, minisiteRefNote } from "@/lib/minisiteInstructions";

// One API key per mini-site install, for GET /api/minisite/access (answers signed with the key).
export const MinisiteKeysCard = () => {
  const { t, fmtDate } = useI18n();
  const [keys, setKeys] = useState([]);
  const [label, setLabel] = useState("");
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/minisite/keys").then(({ data }) => setKeys(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/minisite/keys", { label });
      setFresh({ id: data.id, key: data.key });
      setLabel("");
      load();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  const revoke = (id) => api.delete(`/admin/minisite/keys/${id}`).then(load).catch((err) => toast.error(translateError(err, t)));
  return (
    <section className="card-carbon min-w-0 space-y-3 rounded-2xl p-5" data-testid="minisite-keys-card">
      <h2 className="flex items-center gap-2 font-display text-base uppercase tracking-wider text-[var(--gold-bright)] md:text-lg"><KeyRound className="h-5 w-5 shrink-0" /> <span className="min-w-0 break-words">{t("keys.title")}</span></h2>
      <p className="break-words text-xs text-neutral-400">{t("keys.hint")}</p>
      <CopyButton text={() => minisiteInstructions(window.location.origin)} label={t("keys.copyInstructions")}
        className="btn-gold btn-cta w-full justify-center sm:w-auto" testId="minisite-copy-instructions" />
      <CopyButton text={() => minisiteRefNote(window.location.origin)} label={t("keys.copyRefNote")}
        className="btn-gold btn-sm w-full justify-center sm:w-auto" testId="minisite-copy-ref-note" />
      <form onSubmit={create} className="flex min-w-0 flex-wrap gap-2">
        <Input required maxLength={80} value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("keys.label")} className="min-w-0 flex-1 basis-48 bg-black/50" data-testid="minisite-key-label" />
        <button type="submit" className="btn-gold btn-sm" disabled={busy} data-testid="minisite-key-create">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("keys.create")}</button>
      </form>
      {fresh && (
        <div className="min-w-0 space-y-1 rounded-xl border border-emerald-500/60 bg-emerald-950/40 p-3" data-testid="minisite-key-fresh">
          <p className="break-words text-xs text-emerald-100">{t("keys.once")}</p>
          <div className="flex min-w-0 items-center gap-2">
            <code className="min-w-0 flex-1 break-all text-xs text-white" data-testid="minisite-key-value">{fresh.key}</code>
          </div>
          <CopyButton text={fresh.key} label={t("keys.copyKey")} className="btn-gold btn-sm" testId="minisite-key-copy" />
        </div>
      )}
      <ul className="space-y-2">
        {keys.map((k) => (
          <li key={k.id} className="flex min-w-0 flex-wrap items-center justify-between gap-2 rounded-xl border border-neutral-800 p-3 text-xs" data-testid={`minisite-key-${k.id}`}>
            <span className="min-w-0 break-words text-white">{k.label} · <code className="text-neutral-400">{k.prefix}…</code></span>
            <span className="text-neutral-500">{t("keys.used", { n: k.calls, date: k.last_used ? fmtDate(k.last_used) : "—" })}</span>
            <div className="flex flex-wrap gap-2">
              {fresh?.id === k.id && <CopyButton text={fresh.key} label={t("keys.copyKey")} className="btn-gold btn-sm" testId={`minisite-key-copy-${k.id}`} />}
              <button type="button" className="btn-red btn-sm" onClick={() => revoke(k.id)} data-testid={`minisite-key-revoke-${k.id}`}><Trash2 className="h-4 w-4" /> {t("keys.revoke")}</button>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
};
